import type { CreateAnalysisResultDto } from '@daevio/shared';

/**
 * Tests started from the home page in this browser (slug → where the test lives in the app). Their public
 * report shows the author's actions there: run it again, open it in the project. Browser storage can be
 * missing or blocked: every access is guarded.
 */
const KEY = 'daevio_analysis_owner_tokens';

export interface OwnedTest {
  token: string;
  projectId: string | null;
  testId: string | null;
}

function readAll(): Record<string, OwnedTest> {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    if (!parsed || typeof parsed !== 'object') return {};
    // Entries written before the projects were known are a bare token.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).map(([slug, v]) => [slug, typeof v === 'string' ? { token: v, projectId: null, testId: null } : (v as OwnedTest)]),
    );
  } catch {
    return {};
  }
}

export function saveOwnedTest(r: Pick<CreateAnalysisResultDto, 'slug' | 'ownerToken' | 'projectId' | 'testId'>): void {
  try {
    const all = readAll();
    all[r.slug] = { token: r.ownerToken, projectId: r.projectId, testId: r.testId };
    // Keep the latest 50: enough for anyone, small in storage.
    const entries = Object.entries(all).slice(-50);
    localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // Storage blocked: the report still works, it just does not show the author's actions in this browser.
  }
}

export function ownedTest(slug: string): OwnedTest | null {
  return readAll()[slug] ?? null;
}
